import { useState } from 'react'
//import { useFunctions } from 'reactfire'
import { Button, Col, Form, Input, Row, Select, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { formGutter } from '../../lib/layout'
import { countries } from '../../config/settings'
import {
  companyTypeOptions,
  profitDeterminationTypeOptions,
} from './companyFormOptions'
import { forcedProfitDeterminationTypes } from './forcedProfitDeterminationTypes'
import { useValidationI18n } from '../../lib/validation/useValidationI18n'
import { phoneNumberRule } from '../../lib/shared/validation/PhoneNumberRule'
import { CompanyContact } from '../../lib/shared/models/Company'
//import { api } from '../../lib/api'

const { Option } = Select

const federalStateOptions = [
  { value: 'BW', label: 'Baden-Württemberg' },
  { value: 'BY', label: 'Bayern' },
  { value: 'BE', label: 'Berlin' },
  { value: 'BB', label: 'Brandenburg' },
  { value: 'HB', label: 'Bremen' },
  { value: 'HH', label: 'Hamburg' },
  { value: 'HE', label: 'Hessen' },
  { value: 'MV', label: 'Mecklenburg-Vorpommern' },
  { value: 'NI', label: 'Niedersachsen' },
  { value: 'NW', label: 'Nordrhein-Westfalen' },
  { value: 'RP', label: 'Rheinland-Pfalz' },
  { value: 'SL', label: 'Saarland' },
  { value: 'SN', label: 'Sachsen' },
  { value: 'ST', label: 'Sachsen-Anhalt' },
  { value: 'SH', label: 'Schleswig-Holstein' },
  { value: 'TH', label: 'Thüringen' },
]

const acceptsContactEmail = (value) =>
  !value?.trim() || CompanyContact.shape.email.safeParse(value).success

const acceptsAdditionalEmails = (value) =>
  !value?.length ||
  CompanyContact.shape.additionalEmails.safeParse(value).success

const CompanyDetailsForm = ({
  isFinalStep,
  data,
  sendFormValues,
  loading,
  onCancel,
  goBack,
  actionsClassName,
}) => {
  const { t } = useTranslation()
  const i18n = useValidationI18n()
  const [form] = Form.useForm()
  //const functions = useFunctions()

  const [disableProfitDeterminationType, setDisableProfitDeterminationType] =
    useState(
      Boolean(forcedProfitDeterminationTypes[data?.accounting?.companyType])
    )
  const [federalStateTouched, setFederalStateTouched] = useState(false)

  // const onValidateAddress = async (values) => {
  //   const addressValidationResult = await api(functions, 'addresses/validate', {
  //     country: values.address.country,
  //     address: `${values.address.street} ${values.address.streetNumber}, ${values.address.zipCode} ${values.address.city} ${values.address.state}`,
  //   })
  // }

  const onFinish = async (values) => {
    // const addressValidationResult = await api(functions, 'addresses/validate', {
    //   country: values.address.country,
    //   address: `${values.address.street} ${values.address.streetNumber}, ${values.address.zipCode} ${values.address.city}`,
    // })

    const finalValues = {
      name: values.name.trim(),
      accounting: {
        companyType: values.accounting.companyType,
        profitDeterminationType: values.accounting.profitDeterminationType,
      },
      address: {
        street: values.address.street.trim(),
        streetNumber: values.address.streetNumber.trim(),
        streetAdditional: values.address.streetAdditional?.trim() || null,
        zipCode: values.address.zipCode.trim(),
        city: values.address.city.trim(),
        country: values.address.country.trim(),
        ...(federalStateTouched && {
          federalState: values.address.federalState || null,
        }),
      },
      contact: {
        name: values.contact?.name?.trim() || null,
        phone: values.contact?.phone?.trim() || null,
        email: values.contact?.email?.trim() || null,
        additionalEmails: (values.contact?.additionalEmails ?? [])
          .map((entry) => entry.trim())
          .filter(Boolean),
      },
    }

    sendFormValues(finalValues, isFinalStep, data?.uid)
  }

  const handleFormValuesChange = (changedValues) => {
    let companyType = changedValues?.accounting?.companyType
    if (typeof companyType !== 'undefined') {
      const profitDeterminationType =
        forcedProfitDeterminationTypes[companyType]
      if (profitDeterminationType) {
        form.setFieldsValue({
          accounting: {
            profitDeterminationType,
          },
        })
        setDisableProfitDeterminationType(true)
      } else {
        setDisableProfitDeterminationType(false)
      }
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={data}
      onValuesChange={handleFormValuesChange}
      onFinish={onFinish}
    >
      <Row gutter={formGutter}>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.companyType')}
            name={['accounting', 'companyType']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.required'),
              },
            ]}
          >
            <Select>
              {companyTypeOptions.map(({ value, labelKey }) => (
                <Option key={value} value={value}>
                  {t(labelKey)}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.profitDetermination')}
            name={['accounting', 'profitDeterminationType']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.required'),
              },
            ]}
          >
            <Select disabled={disableProfitDeterminationType}>
              {profitDeterminationTypeOptions.map(({ value, labelKey }) => (
                <Option key={value} value={value}>
                  {t(labelKey)}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            label={t('frontend:company.companyName')}
            name={['name']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.required'),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={18} md={8}>
          <Form.Item
            label={t('frontend:company.street')}
            name={['address', 'street']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.required'),
              },
              {
                pattern: new RegExp(/^.*[a-zA-ZäöüÄÖÜß].*$/),
                message: t('frontend:company.streetMustContainLetter'),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={6} md={4}>
          <Form.Item
            label={t('frontend:company.streetNumber')}
            name={['address', 'streetNumber']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.requiredCapitalized'),
              },
              {
                pattern: /^\d/,
                message: t('frontend:company.streetNumberMustStartWithDigit'),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.addressAdditional')}
            name={['address', 'streetAdditional']}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={8} md={6}>
          <Form.Item
            label={t('frontend:company.zipCode')}
            name={['address', 'zipCode']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.required'),
              },
              { len: 5, message: t('frontend:company.zipCodeLength') },
              {
                pattern: new RegExp(/^[0-9]*$/i),
                message: t('frontend:company.digitsOnlyAlt'),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={16} md={6}>
          <Form.Item
            label={t('frontend:company.city')}
            name={['address', 'city']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.required'),
              },
              {
                pattern: new RegExp(/^.*[a-zA-ZäöüÄÖÜß].*$/),
                message: t('frontend:company.cityMustContainLetter'),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.country')}
            name={['address', 'country']}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('frontend:company.required'),
              },
            ]}
          >
            <Select showSearch>
              {countries.map((country) => (
                <Option
                  key={country.code.toLowerCase()}
                  value={country.code.toLowerCase()}
                >
                  {country.country}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.federalState')}
            name={['address', 'federalState']}
          >
            <Select
              allowClear
              showSearch
              placeholder={t('frontend:company.federalStateAutoDetected')}
              onChange={() => setFederalStateTouched(true)}
            >
              {federalStateOptions.map((state) => (
                <Option key={state.value} value={state.value}>
                  {state.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.contactPerson')}
            name={['contact', 'name']}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.phoneNumber')}
            name={['contact', 'phone']}
            rules={[phoneNumberRule(i18n, data?.contact?.phone)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.contactEmail')}
            name={['contact', 'email']}
            rules={[
              {
                validator: (_, value) =>
                  acceptsContactEmail(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(t('frontend:company.contactEmailInvalid'))
                      ),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t('frontend:company.additionalContactEmails')}
            name={['contact', 'additionalEmails']}
            rules={[
              {
                validator: (_, value) =>
                  acceptsAdditionalEmails(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(t('frontend:company.contactEmailInvalid'))
                      ),
              },
            ]}
          >
            <Select mode="tags" tokenSeparators={[',', ' ']} open={false} />
          </Form.Item>
        </Col>
        <Col
          xs={24}
          style={{ textAlign: 'right' }}
          className={actionsClassName}
        >
          <Space>
            {onCancel && (
              <Button onClick={onCancel}>{t('frontend:common.cancel')}</Button>
            )}
            {goBack && (
              <Button type="text" onClick={goBack}>
                {t('frontend:common.back')}
              </Button>
            )}
            <Button type="primary" htmlType="submit" loading={loading}>
              {isFinalStep
                ? t('frontend:company.save')
                : t('frontend:company.next')}
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  )
}

export default CompanyDetailsForm
